import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { sealPlaintext, openCiphertext } from "@/lib/vault-crypto";
import {
    Lock, Loader2, ShieldCheck, KeyRound, Trash2, EyeOff, AlertTriangle,
} from "lucide-react";

export default function Vault() {
    const [entries, setEntries] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    // New entry form
    const [label, setLabel] = useState("");
    const [content, setContent] = useState("");
    const [passphrase, setPassphrase] = useState("");
    const [confirm, setConfirm] = useState("");

    // Open modal state
    const [openTarget, setOpenTarget] = useState(null);
    const [openPassphrase, setOpenPassphrase] = useState("");
    const [openPlaintext, setOpenPlaintext] = useState("");
    const [openErr, setOpenErr] = useState("");
    const [opening, setOpening] = useState(false);

    const load = async () => {
        const { data } = await api.get("/vault");
        setEntries(data.entries || []);
    };
    useEffect(() => { load(); }, []);

    const seal = async () => {
        setErr("");
        if (!label.trim()) return setErr("A label is required (so you can find it later).");
        if (!content) return setErr("Nothing to seal.");
        if (!passphrase || passphrase.length < 8) return setErr("Passphrase must be at least 8 characters.");
        if (passphrase !== confirm) return setErr("Passphrases do not match.");
        setBusy(true);
        try {
            const blob = await sealPlaintext(passphrase, content);
            await api.post("/vault", { label: label.trim(), ...blob });
            setLabel("");
            setContent("");
            setPassphrase("");
            setConfirm("");
            load();
        } catch (e) {
            setErr(e.message || "Sealing failed");
        } finally {
            setBusy(false);
        }
    };

    const openEntry = async () => {
        setOpenErr("");
        setOpenPlaintext("");
        setOpening(true);
        try {
            const { data } = await api.get(`/vault/${openTarget.id}`);
            const text = await openCiphertext(openPassphrase, {
                ciphertext: data.ciphertext, iv: data.iv, salt: data.salt,
            });
            setOpenPlaintext(text);
        } catch (e) {
            setOpenErr(e.message || "Could not open");
        } finally {
            setOpening(false);
        }
    };

    const closeOpenModal = () => {
        setOpenTarget(null);
        setOpenPassphrase("");
        setOpenPlaintext("");
        setOpenErr("");
    };

    const remove = async (id, lbl) => {
        if (!window.confirm(`Burn "${lbl}" forever? This cannot be undone.`)) return;
        await api.delete(`/vault/${id}`);
        load();
    };

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Sealed Records · Private Moments</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <Lock className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        The Vault<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        For things the Ledger doesn't get to read. Encrypted in your browser
                        before it leaves you. Even the Shed can't open it. Even Agent 3 can't.
                        Lose the passphrase and the file is dust. That's the contract.
                    </p>
                </div>
                <div className="pencil text-lg transform rotate-2 max-w-xs text-right">
                    "Some drawers are locked from the inside."
                </div>
            </div>

            {/* Trust statement */}
            <div className="paper-card border-2 border-stamp-red p-4 mb-6 flex items-start gap-3" data-testid="vault-trust-banner">
                <ShieldCheck className="w-5 h-5 text-stamp-red flex-shrink-0 mt-0.5" />
                <div className="font-body text-sm text-ink leading-relaxed">
                    <strong className="stamp-red">Zero-knowledge.</strong>{" "}
                    Encryption (AES-GCM-256, PBKDF2-SHA256, 200,000 iterations) runs in your browser before any
                    bytes leave. The server stores ciphertext, IV, and salt only — never the
                    passphrase, never the plaintext, never a way to recover either. If you forget
                    the passphrase, the entry is gone. The Vault cannot help you. Neither can I.
                </div>
            </div>

            {/* New entry */}
            <div className="paper-card tape-corner p-5 mb-8 tilt-l" data-testid="vault-new-form">
                <div className="flex items-center gap-2 label-typed tw-prefix mb-3">
                    <KeyRound className="w-3 h-3" /> Seal a new private record
                </div>

                <label className="label-typed block mb-1">Label (visible to you, plaintext)</label>
                <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. 'Letter for the morning' / 'Diagnosis notes' / 'Things only I know'"
                    className="w-full px-3 py-2 text-sm font-body mb-3"
                    data-testid="vault-label-input"
                />

                <label className="label-typed block mb-1">Content</label>
                <textarea
                    rows={8}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Type or paste the private moment here. It never leaves this browser unencrypted."
                    className="w-full px-3 py-2 text-sm font-body mb-3"
                    data-testid="vault-content-input"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className="label-typed block mb-1">Passphrase (8+ characters)</label>
                        <input
                            type="password"
                            value={passphrase}
                            onChange={(e) => setPassphrase(e.target.value)}
                            placeholder="Choose carefully. There is no reset."
                            autoComplete="new-password"
                            className="w-full px-3 py-2 text-sm font-body"
                            data-testid="vault-passphrase-input"
                        />
                    </div>
                    <div>
                        <label className="label-typed block mb-1">Confirm passphrase</label>
                        <input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            autoComplete="new-password"
                            className="w-full px-3 py-2 text-sm font-body"
                            data-testid="vault-confirm-input"
                        />
                    </div>
                </div>

                {err && (
                    <div className="border border-stamp-red text-stamp-red label-typed px-3 py-2 mb-3" data-testid="vault-error">
                        {err}
                    </div>
                )}

                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="label-typed text-[9px] flex items-center gap-1 text-ink-soft">
                        <AlertTriangle className="w-3 h-3" />
                        No reset. No recovery. Write the passphrase somewhere safe.
                    </div>
                    <button
                        onClick={seal}
                        disabled={busy}
                        className="bg-ink text-paper-light hover:bg-stamp-red px-5 py-2 font-heading uppercase text-xs tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="vault-seal-button"
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                        Seal
                    </button>
                </div>
            </div>

            {/* Sealed list */}
            <div className="paper-card p-4" data-testid="vault-list">
                <div className="label-typed tw-prefix mb-3">
                    Sealed records · {entries.length}
                </div>
                {entries.length === 0 ? (
                    <div className="text-ink-faint label-typed italic">
                        Nothing in the vault. The drawer is empty by design.
                    </div>
                ) : (
                    <ul className="divide-y divide-ink/15">
                        {entries.map((e) => (
                            <li key={e.id} className="flex items-center gap-3 py-3" data-testid={`vault-entry-${e.id}`}>
                                <Lock className="w-4 h-4 text-stamp-red flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-body text-sm text-ink truncate">{e.label}</div>
                                    <div className="label-typed text-[9px]">
                                        Sealed {e.created_at?.replace("T", " ").slice(0, 16)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setOpenTarget(e)}
                                    className="border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-3 py-1 font-heading uppercase text-[10px] tracking-[0.22em]"
                                    data-testid={`vault-open-${e.id}`}
                                >
                                    Open
                                </button>
                                <button
                                    onClick={() => remove(e.id, e.label)}
                                    className="text-ink-soft hover:text-stamp-red"
                                    title="Burn forever"
                                    data-testid={`vault-burn-${e.id}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Open modal */}
            {openTarget && (
                <div
                    className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4"
                    data-testid="vault-open-modal"
                >
                    <div className="paper-card tape-corner max-w-2xl w-full p-6">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="label-typed tw-prefix">Sealed record</div>
                                <div className="font-heading text-2xl uppercase tracking-[0.04em] text-ink mt-1">
                                    {openTarget.label}
                                </div>
                                <div className="label-typed text-[9px]">
                                    Sealed {openTarget.created_at?.replace("T", " ").slice(0, 16)}
                                </div>
                            </div>
                            <button
                                onClick={closeOpenModal}
                                className="text-ink-soft hover:text-stamp-red font-heading uppercase text-[10px] tracking-[0.25em]"
                                data-testid="vault-modal-close"
                            >
                                Close
                            </button>
                        </div>

                        {!openPlaintext ? (
                            <>
                                <label className="label-typed block mb-1">Passphrase</label>
                                <input
                                    type="password"
                                    autoFocus
                                    value={openPassphrase}
                                    onChange={(e) => setOpenPassphrase(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && openEntry()}
                                    placeholder="Enter the passphrase you set when sealing"
                                    className="w-full px-3 py-2 text-sm font-body mb-3"
                                    data-testid="vault-open-passphrase-input"
                                />
                                {openErr && (
                                    <div className="border border-stamp-red text-stamp-red label-typed px-3 py-2 mb-3" data-testid="vault-open-error">
                                        {openErr}
                                    </div>
                                )}
                                <div className="flex justify-end">
                                    <button
                                        onClick={openEntry}
                                        disabled={opening || !openPassphrase}
                                        className="bg-ink text-paper-light hover:bg-stamp-red px-5 py-2 font-heading uppercase text-xs tracking-[0.25em] inline-flex items-center gap-2 disabled:opacity-50"
                                        data-testid="vault-open-decrypt"
                                    >
                                        {opening ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
                                        Open
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <pre
                                    className="font-body text-sm text-ink whitespace-pre-wrap break-words bg-paper-light/60 border border-ink/15 p-4 max-h-96 overflow-auto select-text"
                                    data-testid="vault-plaintext"
                                >
                                    {openPlaintext}
                                </pre>
                                <div className="flex items-center justify-between mt-3">
                                    <div className="label-typed text-[9px] flex items-center gap-1 text-ink-soft">
                                        <EyeOff className="w-3 h-3" />
                                        Plaintext exists only on this device, only while open.
                                    </div>
                                    <button
                                        onClick={closeOpenModal}
                                        className="border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper-light px-4 py-1.5 font-heading uppercase text-[11px] tracking-[0.22em]"
                                        data-testid="vault-modal-reseal"
                                    >
                                        Re-seal & close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </Shell>
    );
}
